import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsApi, isPhoneGateError, errorDetailText } from '../services/api';
import { CreateJobRequest, ResearchJob } from '../types';

/**
 * Shared research-job creation flow — one implementation for the intake page
 * (NewResearch) and the global chat pane (ChatPane), so quota 403s, the
 * free-tier phone gate (PhoneVerifyModal retry) and post-create navigation
 * behave identically everywhere FramingChat's onAccept fires.
 *
 * `onCreated` runs just before navigation; returning `true` requests a
 * replace-navigation (the mobile chat sheet pushes a same-URL history sentinel
 * for back-button dismissal — replacing it keeps the back stack clean).
 */
export function useCreateResearchJob(onCreated?: (job: ResearchJob) => boolean | void) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Free-tier phone gate: the blocked request is held here and retried the
  // moment the OTP modal reports the phone as verified.
  const [pendingJob, setPendingJob] = useState<CreateJobRequest | null>(null);

  const submit = async (data: CreateJobRequest) => {
    setIsSubmitting(true);
    setError('');
    try {
      const { data: job } = await jobsApi.createJob(data);
      const replace = onCreated?.(job as ResearchJob) === true;
      // Pass the created job so JobDetail renders the progress UI immediately,
      // without a blank skeleton while it re-fetches.
      navigate(`/app/research/${(job as ResearchJob).id}`, { state: { job }, replace });
    } catch (err: any) {
      if (isPhoneGateError(err)) {
        setPendingJob(data);
      } else {
        setError(errorDetailText(err, 'Failed to start research. Please try again.'));
      }
    } finally {
      // Reset even on success: the chat pane stays mounted after navigation
      // (NewResearch unmounts, where this is a harmless no-op).
      setIsSubmitting(false);
    }
  };

  /** Spread-ready props for <PhoneVerifyModal>. */
  const phoneModal = {
    open: pendingJob !== null,
    onClose: () => setPendingJob(null),
    onVerified: () => {
      const data = pendingJob;
      setPendingJob(null);
      if (data) submit(data);
    },
  };

  return { isSubmitting, error, submit, phoneModal };
}
